import { Link, useNavigate, useParams } from "react-router-dom";
import { DateTimePicker } from "@/components/appointmentRequest/dateTimePicker";
import { useEffect, useState } from "react";
import formatISO from "date-fns/formatISO";
import getDay from "date-fns/getDay";
import { useTranslation } from "react-i18next";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";
import dayjs from "dayjs";
import { api } from "@/helper/api";
import { Breadcrumbs, Grid, Typography } from "@mui/material";
import CheckBox from "@/components/forms/inputs/checkBox";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import { useSelector } from "react-redux";
import { RootState } from "@/components/state/store";
import styles from "@/components/appointmentRequest/appointmentRequestDateTime.module.css";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import GenericTime from "@/components/dateTime/genericTime";
import useFocusFirstInvalidField from "@/helper/customHooks/useFocusFirstInvalidField";
import useFormSubmission from "@/helper/customHooks/useFormSubmission";

export function AppointmentRequestDateTime() {
  const [startTime, setStarttime] = useState<any>(
    dayjs().hour(8).minute(0).second(0).millisecond(0)
  );
  const [endTime, setEndtime] = useState<any>(
    dayjs().hour(16).minute(0).second(0).millisecond(0)
  );
  const [initialDateRange, setInitialDateRange] = useState<
    [Date | null, Date | null]
  >([null, null]);
  const [dateRange, setDateRange] = useState<Date[]>([]);
  const [appointmentRequestData, setAppointmentRequestData] = useState<any>();
  const [skipValidation, setSkipValidation] = useState<boolean>(false);
  const [appointmentRequestStatus, setAppointmentRequestStatus] =
    useState<string>("");
  const [dateRangeError, setDateRangeError] = useState(false);
  const { registerValidationTarget, focusFirstInvalidField } =
    useFocusFirstInvalidField<Record<string, never>, "dateRange">({
      fieldOrder: [],
    });
  const { handleError, errorModal } = useErrorModal();

  const { appointmentrequestId } = useParams() as {
    appointmentrequestId: string;
  };
  const { showConfirmationModalAsync } = useConfirmationModal();

  const user = useSelector((state: RootState) => state.login);

  /*eslint-disable */
  const { t, i18n } = useTranslation();
  const companyName = appointmentRequestData?.company_subject?.name;
  usePageHeading(
    companyName ? `${t("select_date")} - ${companyName}` : t("select_date"),
  );
  /*eslint-enable */

  useEffect(() => {
    if (appointmentrequestId) {
      api.genericApiRequest({
        entity: "appointmentRequest",
        method: "get",

        successHandler: (res: any) => {
          setAppointmentRequestData(res.data);
          setAppointmentRequestStatus(res.data.status);
          const daySet = res.data.day_set;
          if (daySet.length !== 0) {
            const length = daySet.length;
            const start = daySet[0].start;
            const end = daySet[length - 1].end;
            setStarttime(dayjs(start));
            setEndtime(dayjs(end));
            setInitialDateRange([dayjs(start).toDate(), dayjs(end).toDate()]);
          }
        },
        failHandler: (error: any) => {
          handleError(error);
        },
        entityId: appointmentrequestId,
      });
    }
  }, [appointmentrequestId]);

  const navigate = useNavigate();

  const handleDateRange = (range: any) => {
    setDateRange(range);
    setDateRangeError(false);
  };
  const editSkipValidation = async () => {
    if (disableButtonCheck) {
      setDateRangeError(true);
      focusFirstInvalidField({}, ["dateRange"]);
      return;
    }

    const config = {
      headers: {
        "Content-Type": "application/json",
      },
    };

    const result = {
      skip_validation: skipValidation,
    };

    try {
      // eslint-disable-next-line
      const response = await new Promise((resolve, reject) => {
        api.genericApiRequest({
          entity: "appointmentRequest",
          method: "put",
          successHandler: (res: any) => {
            resolve(res);
            navigate("/appointmentrequest");
          },
          failHandler: (error: any) => {
            reject(error);
          },
          entityId: appointmentrequestId,
          submitData: result,
          config: config,
        });
      });
    } catch (error) {
      handleError(error);
    }
  };
  const editAppointmentRequest = async () => {
    if (disableButtonCheck) {
      setDateRangeError(true);
      focusFirstInvalidField({}, ["dateRange"]);
      return;
    }

    if (
      appointmentrequestId &&
      appointmentRequestStatus === "open" &&
      !skipValidation
    ) {
      navigate("/appointmentrequest");
    } else {
      const today = dayjs().format("YYYY-MM-DD");
      const startDay = dayjs(dateRange[0]).format("YYYY-MM-DD");

      const startHours = startTime.$H < 10 ? "0" + startTime.$H : startTime.$H;
      const startMinutes =
        startTime.$m < 10 ? "0" + startTime.$m : startTime.$m;
      const endHours = endTime.$H < 10 ? "0" + endTime.$H : endTime.$H;
      const endMinutes = endTime.$m < 10 ? "0" + endTime.$m : endTime.$m;
      const daySet = [];

      let range = [];
      if (dateRange instanceof Date) {
        range.push(dateRange);
      } else {
        range = dateRange;
      }
      for (const key in range) {
        const date = range[key];
        const dateToIso = formatISO(date, { representation: "date" }) + "T";

        daySet.push({
          start: dateToIso + startHours + ":" + startMinutes,
          end: dateToIso + endHours + ":" + endMinutes,
        });
      }

      if (user.companyName === "kaer" && startDay === today) {
        const isConfirmed = await showConfirmationModalAsync(
          t("confirm_create_appointmentrequest_for_today")
        );
        if (!isConfirmed) return;
      }
      const result = {
        day_set: daySet,
        skip_validation: skipValidation,
      };

      const config = {
        headers: {
          "Content-Type": "application/json",
        },
      };
      try {
        // eslint-disable-next-line
        const response = await new Promise((resolve, reject) => {
          api.genericApiRequest({
            entity: "appointmentRequest",
            method: "put",
            successHandler: (res: any) => {
              resolve(res);
              navigate("/appointmentrequest");
            },
            failHandler: (error: any) => {
              reject(error);
            },
            entityId: appointmentrequestId,
            submitData: result,
            config: config,
          });
        });
      } catch (error) {
        handleError(error);
      }
    }
  };
  const handleCheckoxChange = (checked: any) => {
    setSkipValidation(checked);
    setDateRangeError(false);
  };
  let hoursOfDay: any;
  if (startTime && endTime) {
    hoursOfDay = endTime.$H - startTime.$H;
  }
  let calculateDays = 0;

  if (appointmentRequestData && hoursOfDay) {
    calculateDays =
      (appointmentRequestData.subject_count *
        appointmentRequestData.slot_duration) /
      60;
    if (appointmentRequestData.parallel_slots > 1) {
      calculateDays = calculateDays / appointmentRequestData.parallel_slots;
    }
    calculateDays = Math.ceil(calculateDays / hoursOfDay);
  }

  const isWeekday = (date: any) => {
    const day = getDay(date);
    return day !== 0 && day !== 6;
  };

  let selectedDays: any;
  if (!dateRange.length) {
    selectedDays = 0;
  } else {
    selectedDays = dateRange.length;
  }
  // const days = dateRange.length > 1 ? t("days") : t("day");

  const disableButtonCheck =
    (Object.keys(dateRange).length === 0 || dateRange.length > 1) &&
    selectedDays < calculateDays &&
    !skipValidation;
  const { handleSubmit, isSubmitting } = useFormSubmission({
    onSubmit: editAppointmentRequest,
  });

  const checkAndSetEnttime = (time: any) => {
    if (startTime && time >= startTime) {
      setEndtime(time);
    } else {
      alert(t("end_time_before_start_time"));
      return;
    }
  };

  return (
    <>
      {appointmentRequestData && (
        <form
          className={styles.mobileTimePickerWrapper}
          onSubmit={handleSubmit}
          noValidate>
          {appointmentrequestId && appointmentRequestStatus !== "draft" && (
            <div role="presentation">
              <Breadcrumbs separator="|">
                <Link
                  className={styles.linkNoUnderline}
                  to={`/editappointmentrequest/${appointmentrequestId}/settings`}
                >
                  {t("basic_information")}
                </Link>
                <Link
                  className={styles.linkNoUnderline}
                  to={`/editappointmentrequest/${appointmentrequestId}/building`}
                >
                  {t("building_location")}
                </Link>
                <Link
                  className={styles.linkUnderlined}
                  to={`/editappointmentrequest/${appointmentrequestId}/calendar`}
                >
                  {t("period")}
                </Link>
              </Breadcrumbs>
            </div>
          )}
          <GenericTime
            value={startTime}
            clearable={false}
            onChange={(value) => setStarttime(value)}
            disabled={appointmentRequestStatus !== "draft" ? true : false}
            textFieldProps={{
              label: t("start_time"),
              required: true,
            }}
          />
          <GenericTime
            value={endTime}
            clearable={false}
            onChange={(value) => checkAndSetEnttime(value)}
            disabled={appointmentRequestStatus !== "draft" ? true : false}
            textFieldProps={{
              label: t("end_time"),
              required: true,
            }}
          />
          <Grid
            container
            className={styles.datePickerWrapper}
            ref={registerValidationTarget("dateRange")}>
            <DateTimePicker
              selectsRange={true}
              onlyWeekDays={true}
              initialDateRange={initialDateRange}
              filterDate={isWeekday}
              handleDateRange={handleDateRange}
              calculatedDays={calculateDays}
              selectedDays={selectedDays}
              disabled={appointmentRequestStatus !== "draft" ? true : false}
            />
            <Grid item>
              {(selectedDays < calculateDays ||
                appointmentRequestStatus !== "draft") && (
                <>
                  <h4 style={{ fontSize: "1rem", margin: "0" }}>
                    {t("proceed_with_less_days")}
                  </h4>
                  <CheckBox
                    label={t("yes")}
                    labelClassName={styles.checkBoxLabel}
                    onChange={handleCheckoxChange}
                    data-testid="proceedWithLessDays"
                  />
                </>
              )}
            </Grid>
            {dateRangeError && (
              <Typography color="error" variant="caption" role="alert">
                {t("field_required")}
              </Typography>
            )}
          </Grid>
          <div className={styles.buttonWrapper}>
            <GenericButton
              data-testid="appointment-request-date-save-button"
              type="submit"
              disabled={isSubmitting}
              loading={isSubmitting}
              className={styles.submitButton}
              waitingForResponse={true}
              showSpinnerOnDisable
            >
              {appointmentrequestId && appointmentRequestStatus === "open"
                ? t("back_to", { name: t("appointmentrequest_multi") })
                : t("save")}
            </GenericButton>
            {skipValidation && appointmentRequestStatus !== "draft" && (
              <GenericButton
                disabled={isSubmitting}
                onClick={editSkipValidation}
                className={styles.submitButton}
                waitingForResponse={true}
                showSpinnerOnDisable
              >
                {t("save")}
              </GenericButton>
            )}
          </div>
        </form>
      )}
      {errorModal}
    </>
  );
}
